const BIGBRAIN_SCRIPT_URL = "https://informer-cdn2.monday.com/monday-informer-1.0.min.js";
const BIGBRAIN_URL = "https://data.bigbrain.me";
const WEBSITE_GROUP = "workcanvas";
const WEBFLOW_SOURCE = "workcanvas_product";

interface BigBrainOptions {
  isConnectedViaIframe?: boolean;
  onDone?: () => void;
}

interface TrackerOptions {
  bigbrain_url: string;
  send_immediately: boolean;
  create_visitor: boolean;
  is_tracker_cross_domain?: boolean;
  emit_visitor_id?: boolean;
  emit_target_origin?: string;
}

declare global {
  interface Window {
    BigBrainQ: any[];
    BigBrain: any;
    BigBrainInit: boolean;
    BigBrainTracker: new (options: TrackerOptions) => any;
  }
}

function addGeneralEventProps(args: any[]): any[] {
  let eventProps: any = {};

  if (args.length === 3) {
    eventProps = args[2];
  }

  eventProps.source = eventProps.source || WEBFLOW_SOURCE;
  eventProps.group = WEBSITE_GROUP;

  if (args.length === 2) {
    args.push(eventProps);
  } else {
    args[2] = eventProps;
  }

  return args;
}

function loadScript(url: string, onLoad: () => void): void {
  if (window.BigBrainInit) return;

  window.BigBrainInit = true;
  const script = document.createElement("script");
  script.type = "text/javascript";
  script.src = url;
  script.async = true;

  const handleLoad = () => {
    if (!handleLoad.done) {
      handleLoad.done = true;
      onLoad();
    }
  };
  handleLoad.done = false;

  script.addEventListener("readystatechange", handleLoad);
  script.onload = handleLoad;

  (document.body || document.head).append(script);
}

export const initBigbrain = (options: BigBrainOptions = {}): void => {
  const { onDone } = options;

  // Initialize BigBrain queue and tracking function
  window.BigBrainQ = window.BigBrainQ || [];
  window.BigBrain =
    window.BigBrain ||
    function (...args: any[]) {
      if (args[0] === "track") {
        args = addGeneralEventProps(args);
      }
      window.BigBrainQ.push(args);
    };

  // Load the BigBrain script
  loadScript(BIGBRAIN_SCRIPT_URL, () => {
    const trackerOptions: TrackerOptions = {
      bigbrain_url: BIGBRAIN_URL,
      send_immediately: true,
      create_visitor: true,
    };

    // Set tracker options for cross-domain tracking
    trackerOptions.is_tracker_cross_domain = true;
    trackerOptions.emit_visitor_id = true;
    trackerOptions.emit_target_origin = "*";

    window.BigBrain = new window.BigBrainTracker(trackerOptions);

    onDone?.();
  });
};

/**
 * Initialize BigBrain user parameters
 * Should be called after BigBrain is initialized and user is authenticated
 */
export const initBigBrainUser = (userId: number, accountId: number): void => {
  if (!window.BigBrain) {
    console.warn("BigBrain not initialized. Cannot set user parameters.");
    return;
  }

  window.BigBrain("set_user_id", userId);
  window.BigBrain("set_account_id", accountId);
};

export const trackBigbrain = (eventName: string, data: any, kind: string | undefined) => {
  if (!window.BigBrain) {
    console.log("BigBrain not initialized. Cannot track event.");
    return;
  }
  window.BigBrain("track", eventName, {
    source: WEBFLOW_SOURCE,
    group: WEBSITE_GROUP,
    data,
    kind: kind || "workcanvas",
  });
};
